import React, { useState, useEffect } from 'react';
import { Link } from '@tanstack/react-router';
import Header from './Home/components/Header';
import { 
  ChevronDownIcon,
  CreditCardIcon,
  CogIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import articles from '../articles';
import { useSEO } from '../hooks/useSEO';

const getCategoryIcon = (category) => {
  const iconMap = {
    billing: CreditCardIcon,
    settings: CogIcon,
    troubleshooting: ExclamationTriangleIcon
  };
  return iconMap[category] || CogIcon;
};

const getTagColor = (tag) => {
  // Use semantic accent color for tags to ensure good contrast in light and dark modes
  // Slight translucent background with readable foreground.
  return 'bg-accent/10 text-accent-foreground';
};

const ArticleSection = ({ category, articles: categoryArticles, isActive, onToggle }) => {
  const [open, setOpen] = useState(isActive || false);

  useEffect(() => {
    if (isActive) {
      setOpen(true);
    }
  }, [isActive]);

  const toggleOpen = () => {
    const newOpenState = !open;
    setOpen(newOpenState);
    if (newOpenState) {
      onToggle(category);
    }
  };

  return (
  <div className="bg-background-soft rounded-lg p-6 mb-8" id={category}>
      <button
        onClick={toggleOpen}
        className="w-full flex justify-between items-center"
      >
        <div className="flex items-center gap-4">
          {(() => {
            const IconComponent = getCategoryIcon(category);
            return <IconComponent className="w-6 h-6 text-accent" />;
          })()}
          <h3 className="text-xl font-semibold text-foreground capitalize">{category}</h3>
          <span className="text-muted-foreground text-sm">({categoryArticles.length} articles)</span>
        </div>
        <ChevronDownIcon className={`w-6 h-6 transform transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="mt-6 space-y-4">
          <div className="grid gap-3">
            {categoryArticles.map((article) => (
              <Link 
                key={article.id} 
                to={`/docs/support/${article.id}`}
                className="bg-background-elevated rounded-lg p-4 hover:bg-background-elevated/90 hover:shadow-lg hover:scale-[1.01] transition-all duration-200 block cursor-pointer group"
              >
                <h4 className="text-lg font-medium text-foreground mb-2 group-hover:text-accent transition-colors">{article.title}</h4>
                <p className="text-muted-foreground text-sm mb-3">{article.description}</p>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4 text-xs text-muted">
                    <span>Updated {article.lastUpdated}</span>
                    {article.tags && (
                      <div className="flex gap-1">
                        {article.tags.map(tag => (
                          <span key={tag} className={`px-2 py-1 rounded text-xs ${getTagColor(tag)}`}>
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <span className="text-accent group-hover:text-accent/90 text-sm font-medium transition-colors">
                    Read article →
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default function SupportDocs() {
  const [activeCategory, setActiveCategory] = useState(null);

  useSEO({
    title: 'Support Documentation - Compress GLB | Help & Troubleshooting',
    description: 'Get help with Compress GLB including troubleshooting guides, billing information, and frequently asked questions about 3D model compression.',
    keywords: 'Compress GLB support, 3D compression help, GLB troubleshooting, GLTF compression FAQ, technical support',
    canonical: 'https://compress-glb.com/docs/support',
    ogImage: '/images/og/docs.png',
    ogTitle: 'Compress GLB Support Documentation',
    ogDescription: 'Find answers to common questions and troubleshooting guides for 3D model compression.',
    structuredData: {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "name": "Compress GLB Support Documentation",
      "description": "Support documentation and troubleshooting guides",
      "url": "https://compress-glb.com/docs/support"
    }
  });

  useEffect(() => {
    const hash = window.location.hash.substring(1);
    if (hash) {
      setActiveCategory(hash);
      setTimeout(() => {
        const element = document.getElementById(hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  }, []);

  const handleCategoryClick = (category) => {
    setActiveCategory(category);
    window.history.pushState(null, null, `#${category}`);
  };

  const categorizedArticles = articles.reduce((acc, article) => {
    if (!acc[article.category]) {
      acc[article.category] = [];
    }
    acc[article.category].push(article);
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Header />
      <div className="flex flex-col items-center">
        <main className="max-w-7xl w-full px-4 sm:px-8 lg:px-16 flex-1 flex gap-8">
          {/* Sticky Sidebar */}
          <div className="w-80 shrink-0 hidden md:block sticky top-20 h-[calc(100vh-90px)] overflow-y-auto">
            <nav className="space-y-4 pr-4 pt-8">
              <div className="bg-background-elevated/50 p-4 rounded-lg">
                <h3 className="text-sm font-semibold mb-2 text-accent">Support Center</h3>
                <p className="text-xs text-muted-foreground">
                  Find answers to common questions and troubleshoot issues
                </p>
              </div>

              <div className="space-y-2">
                <h4 className="text-sm font-semibold text-muted-foreground px-2">Categories</h4>
                <div className="space-y-1">
                  {Object.keys(categorizedArticles).map((category) => {
                    const IconComponent = getCategoryIcon(category);
                    return (
                    <button
                      key={category}
                      onClick={() => handleCategoryClick(category)}
                      className={`flex items-center gap-2 w-full text-left px-2 py-1.5 text-sm rounded hover:bg-background-elevated ${
                        activeCategory === category ? 'bg-background-elevated text-foreground' : 'text-muted-foreground hover:text-foreground'
                      } transition-colors`}
                    >
                      <IconComponent className="w-4 h-4" />
                      <span className="capitalize">{category} ({categorizedArticles[category].length})</span>
                    </button>
                    );
                  })}
                </div>
              </div>

              <div className="border-t border pt-4">
                <h4 className="text-sm font-semibold text-muted-foreground px-2">Other Resources</h4>
                <div className="space-y-1 mt-2">
                  <Link
                    to="/docs/api"
                    className="block px-2 py-1.5 text-sm rounded hover:bg-background-elevated text-accent hover:text-accent/90 transition-colors"
                  >
                    API documentation
                  </Link>
                  <Link
                    to="/app/dashboard"
                    className="block px-2 py-1.5 text-sm rounded hover:bg-background-elevated text-accent hover:text-accent/90 transition-colors"
                  >
                    Dashboard
                  </Link>
                </div>
              </div>
            </nav>
          </div>

          {/* Main Content */}
          <div className="py-8 h-[calc(100vh-90px)] overflow-y-auto">
            <div className="space-y-8">
              <h1 className="text-4xl font-bold text-accent">Support docs</h1>

              <div className="bg-background-soft p-6 rounded-lg">
                <div className="mb-6">
                  <h3 className="text-lg font-semibold mb-2 text-accent">Need help?</h3>
                  <p className="text-muted-foreground">
                    Browse these support articles to find answers to common questions, troubleshoot issues, 
                    and learn how to get the most out of this GLB compression service.
                  </p>
                </div>

                <div className="grid md:grid-cols-3 gap-6">
                  {Object.entries(categorizedArticles).map(([category, categoryArticles]) => {
                    const IconComponent = getCategoryIcon(category);
                    return (
                    <div key={category} className="bg-background-elevated p-4 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <IconComponent className="w-4 h-4 text-accent" />
                        <h4 className="text-sm font-semibold text-accent capitalize">{category}</h4>
                      </div>
                      <p className="text-muted-foreground text-sm mb-3">
                        {categoryArticles.length} article{categoryArticles.length !== 1 ? 's' : ''}
                      </p>
                      <button
                        onClick={() => handleCategoryClick(category)}
                        className="text-accent hover:text-accent/90 text-sm font-medium"
                      >
                        Browse {category} →
                      </button>
                    </div>
                  );
                  })}
                </div>
              </div>

              {Object.entries(categorizedArticles).map(([category, categoryArticles]) => (
                <ArticleSection
                  key={category}
                  category={category}
                  articles={categoryArticles}
                  isActive={activeCategory === category}
                  onToggle={handleCategoryClick}
                />
              ))}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
} 